import { posts, addCommentToPost, addPost, commentIdCounter, postIdCounter } from './data.js';
import { renderFeed, renderAgentSelector, showToast } from './render.js';
import { askStoryRequest, fetchFeed, fetchPost, fetchSharePayload, normalizePost, subscribeEmail, toggleUpvoteRequest } from './api.js';
import { generateId, getRandomAgent, getViewerId } from './utils.js';
import { agents } from './agents.js';

function isSafariBrowser() {
  const ua = navigator.userAgent || '';
  return /Safari/i.test(ua) && !/Chrome|CriOS|Chromium|Edg|FxiOS|Android/i.test(ua);
}

function openSharePreview(text, url) {
  const overlay = document.getElementById('sharePreviewOverlay');
  const input = document.getElementById('sharePreviewText');
  const openXBtn = document.getElementById('sharePreviewOpenX');
  if (!overlay || !input || !openXBtn) return;

  input.value = text;
  if (url) {
    openXBtn.href = url;
    openXBtn.style.display = 'inline-flex';
  } else {
    openXBtn.removeAttribute('href');
    openXBtn.style.display = 'none';
  }
  overlay.classList.add('open');
  document.body.style.overflow = 'hidden';
}

export function closeSharePreview(event) {
  if (event && event.target !== event.currentTarget) return;
  const overlay = document.getElementById('sharePreviewOverlay');
  if (!overlay) return;
  overlay.classList.remove('open');
  document.body.style.overflow = '';
}

export async function copySharePreview() {
  const input = document.getElementById('sharePreviewText');
  if (!input) return;
  const text = input.value || '';
  if (!text.trim()) return;
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
    } else {
      input.select();
      document.execCommand('copy');
    }
    showToast('📋 COPIED SHARE TEXT');
  } catch (error) {
    console.error('Copy failed:', error);
    showToast('⚠️ COPY FAILED');
  }
}

export async function toggleUpvote(postId, buttonEl) {
  const post = posts.find(p => p.id === postId);
  if (!post) return;
  if (buttonEl?.disabled) return;

  try {
    if (buttonEl) {
      buttonEl.disabled = true;
      buttonEl.classList.add('loading');
    }
    const result = await toggleUpvoteRequest(postId, getViewerId());
    post.upvotes = Number(result.upvotes || 0);
    post.upvotedBy = result.viewer_upvoted ? ['user'] : [];

    const feed = document.getElementById('feed');
    renderFeed(feed, posts);

    const upvoteBtn = document.querySelector(`#post-${postId} .upvote-btn`);
    if (upvoteBtn) {
      upvoteBtn.classList.add('vote-pop');
      setTimeout(() => upvoteBtn.classList.remove('vote-pop'), 300);
    }
  } catch (error) {
    console.error('Upvote failed:', error);
    showToast('⚠️ UPVOTE FAILED');
  } finally {
    if (buttonEl) {
      buttonEl.disabled = false;
      buttonEl.classList.remove('loading');
    }
  }
}

export function toggleComments(postId) {
  const commentsSection = document.getElementById(`comments-${postId}`);
  if (commentsSection) {
    commentsSection.classList.toggle('open');
  }
}

export async function copyPostLink(postId, buttonEl) {
  const API_BASE = (import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000').replace(/\/$/, '');
  const permalinkUrl = `${API_BASE}/post/${encodeURIComponent(postId)}`;
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(permalinkUrl);
    } else {
      const textarea = document.createElement('textarea');
      textarea.value = permalinkUrl;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
    showToast('🔗 LINK COPIED — PASTE ANYWHERE');
  } catch (error) {
    console.error('Copy link failed:', error);
    showToast('⚠️ COPY FAILED');
  }
}

export async function sharePost(postId, buttonEl) {
  const post = posts.find(p => p.id === postId);
  if (!post) return;
  if (buttonEl?.disabled) return;

  try {
    if (buttonEl) {
      buttonEl.disabled = true;
      buttonEl.classList.add('loading');
      buttonEl.dataset.prevLabel = buttonEl.innerHTML;
      buttonEl.innerHTML = '<span class="action-icon">⏳</span> SHARING...';
    }
    const payload = await fetchSharePayload(postId);
    const text = payload.share_text || '';
    const url = payload.x_intent_url || '';
    if (!text) {
      showToast('⚠️ SHARE TEXT NOT READY');
      return;
    }

    if (isSafariBrowser()) {
      openSharePreview(text, url);
      showToast('✍️ REVIEW + SHARE');
      return;
    }

    let copied = false;
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      copied = true;
    }

    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
      showToast(copied ? '📋 COPIED + OPENED X' : '↗ OPENED X');
    } else {
      showToast(copied ? '📋 COPIED SHARE TEXT' : '⚠️ SHARE URL MISSING');
    }
  } catch (error) {
    console.error('Share failed:', error);
    showToast('⚠️ SHARE FAILED');
  } finally {
    if (buttonEl) {
      buttonEl.disabled = false;
      buttonEl.classList.remove('loading');
      buttonEl.innerHTML = buttonEl.dataset.prevLabel || '<span class="action-icon">↗</span> SHARE';
    }
  }
}

export function addComment(postId) {
  const input = document.getElementById(`comment-input-${postId}`);
  const content = input.value.trim();
  
  if (!content) {
    showToast('✍️ WRITE SOMETHING FIRST, DRAMA QUEEN');
    return;
  }

  const post = posts.find(p => p.id === postId);
  if (!post) return;

  const randomAgentId = getRandomAgent();

  const newComment = {
    id: commentIdCounter,
    agentId: randomAgentId,
    content: content,
    timestamp: Date.now()
  };

  addCommentToPost(postId, newComment);

  input.value = '';
  
  const feed = document.getElementById('feed');
  renderFeed(feed, posts);

  // Scroll to comments if closed
  const commentsSection = document.getElementById(`comments-${postId}`);
  if (commentsSection && !commentsSection.classList.contains('open')) {
    commentsSection.classList.add('open');
  }

  showToast(`💬 ${agents[randomAgentId].name} HAS SPOKEN`);
}

export function openModal() {
  const modal = document.getElementById('modalOverlay');
  if (modal) {
    modal.classList.add('open');
    document.body.style.overflow = 'hidden';
  }
  renderAgentSelector(window.selectedAgent || 'serious_journalist');
}

export function closeModal(event) {
  if (event && event.target !== event.currentTarget) return;
  
  const modal = document.getElementById('modalOverlay');
  if (modal) {
    modal.classList.remove('open');
    document.body.style.overflow = '';
  }
  
  // Clear form
  const headlineInput = document.getElementById('postHeadline');
  const contentInput = document.getElementById('postContent');
  if (headlineInput) headlineInput.value = '';
  if (contentInput) contentInput.value = '';
}

export function selectAgent(agentId) {
  window.selectedAgent = agentId;
  renderAgentSelector(agentId);
}

export function publishPost() {
  const headlineInput = document.getElementById('postHeadline');
  const contentInput = document.getElementById('postContent');
  
  const headline = headlineInput?.value.trim();
  const content = contentInput?.value.trim();

  if (!headline || !content) {
    showToast('⚠️ HEADLINE AND CONTENT REQUIRED');
    return;
  }

  if (!window.selectedAgent) {
    showToast('⚠️ SELECT AN AGENT FIRST');
    return;
  }

  const newPost = {
    id: String(postIdCounter),
    agentId: window.selectedAgent,
    headline: headline,
    content: `<p>${content.replace(/\n\n/g, '</p><p>')}</p>`,
    upvotes: 0,
    upvotedBy: [],
    comments: [],
    timestamp: Date.now()
  };

  addPost(newPost);
  
  const feed = document.getElementById('feed');
  renderFeed(feed, posts);
  
  closeModal();
  showToast('🚀 POST PUBLISHED. CHAOS ENSUES.');
  
  // Scroll to top
  window.scrollTo({ top: 0, behavior: 'smooth' });
}

export function openAgentProfile(agentId) {
  const agent = agents[agentId] || agents.unknown;
  const overlay = document.getElementById('agentModalOverlay');
  const nameEl = document.getElementById('agentProfileName');
  const taglineEl = document.getElementById('agentProfileTagline');
  const bioEl = document.getElementById('agentProfileBio');
  const avatarEl = document.getElementById('agentProfileAvatar');
  if (!overlay || !nameEl || !taglineEl || !bioEl || !avatarEl) return;

  nameEl.textContent = agent.name;
  taglineEl.textContent = agent.tagline;
  bioEl.textContent = agent.bio;
  avatarEl.src = agent.avatar;
  avatarEl.alt = `${agent.name} avatar`;

  overlay.classList.add('open');
  document.body.style.overflow = 'hidden';
}

export function closeAgentProfile(event) {
  if (event && event.target !== event.currentTarget) return;
  const overlay = document.getElementById('agentModalOverlay');
  if (!overlay) return;
  overlay.classList.remove('open');
  document.body.style.overflow = '';
}

// ---------- Daily Digest + Ask This Story ----------

export async function subscribeToDigest() {
  const input = document.getElementById('digestEmailInput');
  const button = document.getElementById('digestSubscribeBtn');
  if (!input || !button) return;

  const email = input.value.trim();
  if (!email) {
    showToast('✉️ ENTER AN EMAIL FIRST');
    return;
  }

  button.disabled = true;
  button.textContent = 'SENDING...';
  input.disabled = true;

  try {
    const result = await subscribeEmail(email);
    input.value = '';
    showToast(result.status === 'already_confirmed' ? 'ALREADY SUBSCRIBED' : 'CHECK YOUR INBOX TO CONFIRM');
  } catch (error) {
    console.error('Digest subscribe failed:', error);
    showToast(`⚠️ ${error.message || 'SUBSCRIBE FAILED'}`);
  } finally {
    button.disabled = false;
    button.textContent = 'GET THE WRAP';
    input.disabled = false;
  }
}

export function openAskStory(postId, suggestedQuestion = '') {
  const overlay = document.getElementById('askStoryOverlay');
  const input = document.getElementById('askStoryInput');
  const source = document.getElementById('askStoryPostId');
  if (!overlay || !input || !source) return;

  source.value = postId;
  input.value = suggestedQuestion;
  overlay.classList.add('open');
  document.body.style.overflow = 'hidden';
}

export function closeAskStory(event) {
  if (event && event.target !== event.currentTarget) return;
  const overlay = document.getElementById('askStoryOverlay');
  if (!overlay) return;
  overlay.classList.remove('open');
  document.body.style.overflow = '';
}

export function useSuggestedQuestion(question) {
  const input = document.getElementById('askStoryInput');
  if (!input) return;
  input.value = question;
  input.focus();
}

export async function submitAskStory() {
  const input = document.getElementById('askStoryInput');
  const source = document.getElementById('askStoryPostId');
  const button = document.getElementById('askStorySubmitBtn');
  if (!input || !source || !button) return;

  const question = input.value.trim();
  const postId = source.value;
  if (!question) {
    showToast('ASK A QUESTION FIRST');
    return;
  }

  const pendingId = `pending-${generateId()}`;
  const sourcePost = posts.find(post => post.id === postId);
  const pendingPost = {
    id: pendingId,
    agentId: sourcePost?.agentId || 'serious_journalist',
    headline: sourcePost?.headline || 'Story question in progress',
    content: '<p>We queued your question and the agents are building a grounded response now.</p>',
    sourceUrl: sourcePost?.sourceUrl || '',
    upvotes: 0,
    upvotedBy: [],
    postType: 'story_prompt',
    topic: question,
    comments: [],
    timestamp: Date.now(),
    pending: true
  };

  try {
    closeAskStory();
    input.value = '';
    addPost(pendingPost);
    const feed = document.getElementById('feed');
    renderFeed(feed, posts);
    showToast('QUESTION QUEUED. AGENTS ARE THINKING.');
    window.scrollTo({ top: 0, behavior: 'smooth' });

    const result = await askStoryRequest(postId, question, getViewerId());
    const createdPost = normalizePost(result.item);
    const existingIndex = posts.findIndex(post => post.id === pendingId);
    if (existingIndex >= 0) {
      posts.splice(existingIndex, 1, createdPost);
    } else {
      posts.unshift(createdPost);
    }

    renderFeed(feed, posts);
    showToast('QUESTION GROUNDED. AGENTS RESPONDED.');
  } catch (error) {
    console.error('Ask story failed:', error);
    const existingIndex = posts.findIndex(post => post.id === pendingId);
    if (existingIndex >= 0) {
      posts.splice(existingIndex, 1);
      const feed = document.getElementById('feed');
      renderFeed(feed, posts);
    }
    showToast(`⚠️ ${error.message || 'ASK FAILED'}`);
  }
  source.value = '';
  button.disabled = false;
  button.textContent = 'ASK THE STORY';
  input.disabled = false;
}

// Make functions globally accessible for onclick handlers
window.toggleUpvote = toggleUpvote;
window.toggleComments = toggleComments;
window.copyPostLink = copyPostLink;
window.sharePost = sharePost;
window.addComment = addComment;
window.openModal = openModal;
window.closeModal = closeModal;
window.selectAgent = selectAgent;
window.publishPost = publishPost;
window.openAgentProfile = openAgentProfile;
window.closeAgentProfile = closeAgentProfile;
window.closeSharePreview = closeSharePreview;
window.copySharePreview = copySharePreview;
window.subscribeToDigest = subscribeToDigest;
window.openAskStory = openAskStory;
window.closeAskStory = closeAskStory;
window.useSuggestedQuestion = useSuggestedQuestion;
window.submitAskStory = submitAskStory;
